/******************************************************************
 * ##: Location record builder
 * Builds a normalized location record from a GeolocationPosition. Pure helpers,
 * safe for unit testing. Preserves 0 values and coerces missing values to null.
 *
 * TL;DR: Turn a GPS position into a storable locationRecords entry.
 * History:
 * 23-07-2026: Created
 ******************************************************************/

import uniqid from "uniqid";

/**
 * Generate a stable, client-side id for a location record.
 * Prefers crypto.randomUUID (available in secure contexts / HTTPS PWA),
 * falls back to uniqid when unavailable.
 *
 * @returns {string}
 */
const generateUuid = () => {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
  } catch (error) {
    // Fall through to the caller's fallback below.
  }
  return null;
};

export const generateLocationId = () => generateUuid() || uniqid("loc-");

/**
 * Generate a stable client-side reference for an action, created before the location
 * capture so the location can carry it. Kept valid across sync (opaque, never a server _id).
 *
 * @returns {string}
 */
export const generateActionReference = () => generateUuid() || uniqid("act-");

// Keep 0 (a valid reading), turn undefined/null/NaN into null.
const nullableNumber = (value) => {
  if (value === null || value === undefined) return null;
  const num = Number(value);
  return Number.isNaN(num) ? null : num;
};

/**
 * Build a locationRecords entry from a GeolocationPosition.
 *
 * @param {object} params
 * @param {GeolocationPosition} params.position - Browser position (coords + timestamp)
 * @param {string} params.service_id_raw - Active service/trip business id
 * @param {string|null} [params.equipment_id] - Boat id (equipment[0]._id)
 * @param {string|null} [params.action] - Action type (LOCATION_ACTIONS.*) or null for automatic
 * @param {string|null} [params.action_reference] - Stable client action id or null
 * @param {boolean|null} [params.is_online] - Whether the device had internet when the fix was recorded
 * @param {string|null} [params.user] - Logged-in staff id at capture time
 * @param {string|null} [params.device] - Stable tablet id (see utils/location/device.js)
 * @param {string|null} [params.permission_state] - Geolocation permission at capture ("granted"/"denied"/...)
 * @returns {object} A location record ready to persist. A status record (no fix) has null coordinates.
 */
export const buildLocationRecord = ({
  position,
  service_id_raw,
  equipment_id = null,
  action = null,
  action_reference = null,
  is_online = null,
  user = null,
  device = null,
  permission_state = null,
}) => {
  const coords = position?.coords || {};
  const timestamp = position?.timestamp ?? null;
  const recordedAt = timestamp ? new Date(timestamp).toISOString() : new Date().toISOString();

  return {
    id: generateLocationId(),
    service_id_raw: service_id_raw ?? null,
    equipment_id: equipment_id ?? null,
    latitude: nullableNumber(coords.latitude),
    longitude: nullableNumber(coords.longitude),
    accuracy: nullableNumber(coords.accuracy),
    speed: nullableNumber(coords.speed),
    heading: nullableNumber(coords.heading),
    altitude: nullableNumber(coords.altitude),
    recorded_at: recordedAt,
    action: action ?? null,
    action_reference: action_reference ?? null,
    is_online: typeof is_online === "boolean" ? is_online : null,
    user: user ?? null,
    device: device ?? null,
    permission_state: permission_state ?? null,
    synced_at: null,
    created_at: new Date().toISOString(),
  };
};
